import { useState, useEffect } from 'react';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { CreditCard, ShieldCheck, ShieldAlert, CircleOff } from 'lucide-react';

function StatusPill({ ok, yesLabel = 'Yes', noLabel = 'No' }) {
  return (
    <span className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold ${ok ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
      {ok ? yesLabel : noLabel}
    </span>
  );
}

export default function AdminStripeSettings() {
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    api.get('/admin/stripe-settings').then(r => setSettings(r.data)).catch(() => toast.error('Failed to load Stripe status'));
  }, []);

  if (!settings) {
    return <div className="flex items-center justify-center py-20"><div className="w-8 h-8 border-3 border-[#1E4FA1] border-t-transparent rounded-full animate-spin" /></div>;
  }

  return (
    <div data-testid="admin-stripe-settings-page">
      <div className="mb-8">
        <h1 className="text-2xl md:text-3xl font-bold text-[#0F172A]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
          Stripe Settings
        </h1>
        <p className="text-[#94A3B8] text-sm mt-1">Stripe credentials are loaded securely from environment variables only.</p>
      </div>

      <div className="bg-white rounded-2xl border border-[#E2E8F0] p-6 mb-6">
        <div className="flex items-center gap-3">
          <CreditCard size={24} className="text-[#1E4FA1]" />
          <div>
            <h2 className="text-lg font-bold text-[#0F172A]">Stripe Runtime</h2>
            <p className="text-sm text-[#94A3B8]">
              Stripe method activation is managed from the shared Donations page. This screen is status-only.
            </p>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-[#E2E8F0] p-6 space-y-5">
        <h2 className="text-lg font-bold text-[#0F172A]">Runtime Status</h2>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="rounded-2xl border border-[#E2E8F0] p-4 bg-[#F8FAFC]">
            <div className="flex items-center gap-2 mb-2">
              {settings.stripe_configured ? <ShieldCheck size={18} className="text-emerald-600" /> : <ShieldAlert size={18} className="text-amber-600" />}
              <p className="text-sm font-semibold text-[#0F172A]">Stripe Configured</p>
            </div>
            <StatusPill ok={settings.stripe_configured} yesLabel="YES" noLabel="NO" />
          </div>

          <div className="rounded-2xl border border-[#E2E8F0] p-4 bg-[#F8FAFC]">
            <div className="flex items-center gap-2 mb-2">
              {settings.webhook_configured ? <ShieldCheck size={18} className="text-emerald-600" /> : <ShieldAlert size={18} className="text-amber-600" />}
              <p className="text-sm font-semibold text-[#0F172A]">Webhook Configured</p>
            </div>
            <StatusPill ok={settings.webhook_configured} yesLabel="YES" noLabel="NO" />
          </div>

          <div className="rounded-2xl border border-[#E2E8F0] p-4 bg-[#F8FAFC]">
            <div className="flex items-center gap-2 mb-2">
              <CircleOff size={18} className="text-[#1E4FA1]" />
              <p className="text-sm font-semibold text-[#0F172A]">Mode</p>
            </div>
            <span className="inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold bg-[#EAF1F7] text-[#1E4FA1] uppercase">
              {settings.mode || 'unknown'}
            </span>
          </div>
        </div>

        <div className="rounded-2xl border border-[#E2E8F0] p-4 bg-[#F8FAFC]">
          <p className="text-sm font-semibold text-[#0F172A] mb-1">Publishable Key</p>
          <p className="text-sm text-[#475569]">
            {settings.publishable_key_hint || 'Not configured'}
          </p>
          <p className="text-xs text-[#94A3B8] mt-1">Secret values are never displayed here and are read from Render environment variables only.</p>
        </div>

        {!settings.stripe_configured ? (
          <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-700">
            Stripe is not configured. Donations cannot be enabled until `STRIPE_SECRET_KEY` and `STRIPE_PUBLISHABLE_KEY` are set in the backend environment.
          </div>
        ) : null}

        {!settings.webhook_configured ? (
          <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-700">
            `STRIPE_WEBHOOK_SECRET` is missing. Webhook events will be rejected until it is configured.
          </div>
        ) : null}
      </div>
    </div>
  );
}
